import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

const SCRIPT_SRC =
    "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let scriptPromise = null;

const loadTurnstileScript = () => {
    if (window.turnstile) {
        return Promise.resolve(window.turnstile);
    }

    if (!scriptPromise) {
        scriptPromise = new Promise((resolve, reject) => {
            const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
            const script = existing || document.createElement("script");

            if (!existing) {
                script.src = SCRIPT_SRC;
                script.async = true;
                script.defer = true;
                document.head.appendChild(script);
            }

            script.addEventListener("load", () => resolve(window.turnstile));
            script.addEventListener("error", () =>
                reject(new Error("Turnstile script failed to load")),
            );
        });
    }

    return scriptPromise;
};

const Turnstile = forwardRef(function Turnstile({ onVerify, onExpire }, ref) {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);

    useImperativeHandle(ref, () => ({
        reset: () => {
            if (widgetIdRef.current && window.turnstile) {
                window.turnstile.reset(widgetIdRef.current);
            }
        },
    }));

    useEffect(() => {
        let cancelled = false;

        loadTurnstileScript()
            .then((turnstile) => {
                if (cancelled || !containerRef.current || !turnstile) {
                    return;
                }

                widgetIdRef.current = turnstile.render(containerRef.current, {
                    sitekey: process.env.REACT_APP_TURNSTILE_SITE_KEY,
                    action: "turnstile-spin-v2",
                    callback: onVerify,
                    "expired-callback": () => {
                        if (onExpire) onExpire();
                    },
                    "error-callback": () => {
                        if (onExpire) onExpire();
                    },
                });
            })
            .catch((error) => {
                console.error(error);
            });

        return () => {
            cancelled = true;
            if (widgetIdRef.current && window.turnstile) {
                window.turnstile.remove(widgetIdRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!process.env.REACT_APP_TURNSTILE_SITE_KEY) {
        return null;
    }

    return (
        <div
            ref={containerRef}
            className="cf-turnstile"
            data-sitekey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
            data-action="turnstile-spin-v2"
        />
    );
});

export default Turnstile;
